import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Badge,
  Space,
  Button,
  DatePicker,
  Select,
  Input,
  InputNumber,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Tag
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { healthCheckService, HealthCheckLog, HealthCheckStats, HealthCheckSchedulerStatus } from '../services/healthCheckService';
import { serverService, Service as ManagedService } from '../services/serverService';
import { tenantService, Tenant } from '../services/tenantService';
import TimeDisplay from './TimeDisplay';
import { useI18n } from '../hooks/useI18n';

const { RangePicker } = DatePicker;
const { Option } = Select;

type ServiceOption = Pick<ManagedService, 'id' | 'service_name' | 'tenant_code'>;

type FilterState = {
  tenant_code?: string;
  service_id?: number;
  health_status?: string;
  start_date?: string;
  end_date?: string;
};

const HealthCheckLogs: React.FC = () => {
  const [logs, setLogs] = useState<HealthCheckLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<HealthCheckStats | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [filters, setFilters] = useState<FilterState>({
    tenant_code: undefined,
    service_id: undefined,
    health_status: undefined,
    start_date: undefined,
    end_date: undefined
  });
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [schedulerStatus, setSchedulerStatus] = useState<HealthCheckSchedulerStatus | null>(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerToggling, setSchedulerToggling] = useState(false);
  const [retentionDays, setRetentionDays] = useState<number>(10);
  const [retentionDraft, setRetentionDraft] = useState<number | null>(null);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    loadReferenceData();
    loadSchedulerStatus();
    loadRetention();
  }, []);

  const loadRetention = async () => {
    try {
      const r = await healthCheckService.getRetentionDays();
      const d = r?.data?.retentionDays ?? 10;
      setRetentionDays(d);
      setRetentionDraft(d);
    } catch (e) {
      // ignore
    }
  };

  const handleSaveRetention = async () => {
    if (retentionDraft == null) return;
    if (retentionDraft < 1 || retentionDraft > 365) {
      message.error(t('healthCheckLogs.retentionRangeError'));
      return;
    }
    setRetentionSaving(true);
    try {
      const r = await healthCheckService.setRetentionDays(retentionDraft);
      if (r.success) {
        setRetentionDays(r.data.retentionDays);
        setRetentionDraft(r.data.retentionDays);
        message.success(t('healthCheckLogs.retentionSaved', { days: r.data.retentionDays }));
      } else {
        message.error(r.error || t('healthCheckLogs.saveFailed'));
      }
    } catch (e: any) {
      message.error(e?.message || t('healthCheckLogs.saveFailed'));
    } finally {
      setRetentionSaving(false);
    }
  };

  const handleCleanupNow = async () => {
    setCleanupRunning(true);
    try {
      const r = await healthCheckService.cleanupNow();
      if (r.success) {
        const d = r.data;
        message.success(t('healthCheckLogs.cleanupDone', { health: d.healthCheckLogs, email: d.emailLogs, days: d.retentionDays }));
        loadLogs();
        loadStats();
      } else {
        message.error(r.error || t('healthCheckLogs.cleanupFailed'));
      }
    } catch (e: any) {
      message.error(e?.message || t('healthCheckLogs.cleanupFailed'));
    } finally {
      setCleanupRunning(false);
    }
  };

  const loadReferenceData = async () => {
    try {
      const tenantList = await tenantService.getAllTenants();
      setTenants(tenantList);

      if (tenantList.length === 0) {
        setServices([]);
        return;
      }

      const results = await Promise.allSettled(
        tenantList.map((tenant) =>
          serverService.getServices({ tenant_code: tenant.code, limit: 1000 })
        )
      );

      const aggregated: ServiceOption[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const data = result.value.data || result.value.services || [];
          data.forEach((service: ManagedService) => {
            if (service.id !== undefined) {
              aggregated.push({
                id: service.id,
                service_name: service.service_name,
                tenant_code: service.tenant_code || tenantList[index]?.code || ''
              });
            }
          });
        } else {
          console.error('加载服务列表失败:', result.reason);
        }
      });

      const uniqueServices = Array.from(
        aggregated.reduce<Map<number, ServiceOption>>((map, service) => {
          if (service.id !== undefined) {
            map.set(service.id, service);
          }
          return map;
        }, new Map()).values()
      );

      setServices(uniqueServices);
    } catch (error) {
      console.error('加载租户或服务信息失败:', error);
      message.warning(t('healthCheckLogs.loadReferenceDataFailed'));
    }
  };

  const loadSchedulerStatus = async () => {
    setSchedulerLoading(true);
    try {
      const result = await healthCheckService.getSchedulerStatus();
      setSchedulerStatus(result.data);
    } catch (error: any) {
      console.error('加载健康检查调度器状态失败:', error);
      message.warning(t('healthCheckLogs.loadSchedulerFailed'));
    } finally {
      setSchedulerLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const result = await healthCheckService.getHealthCheckLogs({
        tenant_code: filters.tenant_code,
        ...filters,
        page: pagination.current,
        limit: pagination.pageSize
      });
      setLogs(result.data || []);
      setPagination(prev => ({
        ...prev,
        total: result.total || 0
      }));
    } catch (error: any) {
      message.error(t('healthCheckLogs.loadLogsFailed') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await healthCheckService.getHealthCheckStats({
        tenant_code: filters.tenant_code,
        start_date: filters.start_date,
        end_date: filters.end_date
      });
      setStats(result.data);
    } catch (error) {
      console.error('加载统计信息失败:', error);
      message.warning(t('healthCheckLogs.loadStatsFailed'));
    }
  };

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => {
      const next = {
        ...prev,
        [key]: value
      };

      if (key === 'tenant_code') {
        next.service_id = undefined;
      }

      return next;
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const start = new Date(dates[0]);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dates[1]);
      end.setHours(23, 59, 59, 999);
      setFilters(prev => ({
        ...prev,
        start_date: start.toISOString(),
        end_date: end.toISOString()
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        start_date: undefined,
        end_date: undefined
      }));
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleToggleScheduler = async () => {
    setSchedulerToggling(true);
    const running = schedulerStatus?.running ?? false;
    try {
      const action = running ? 'stopScheduler' : 'startScheduler';
      const result = await (healthCheckService as any)[action]();
      if (result.success) {
        message.success(running ? t('healthCheckLogs.schedulerStopped') : t('healthCheckLogs.schedulerStarted'));
        // 成功后立即切换显示
        setSchedulerStatus(prev => ({
          running: !running,
          lastCheckTime: prev?.lastCheckTime ?? null,
          schedule: prev?.schedule ?? '*/15 * * * *',
          cleanupSchedule: prev?.cleanupSchedule ?? '0 2 * * *'
        }));
        try {
          const res = await healthCheckService.getSchedulerStatus();
          if (res?.data) setSchedulerStatus(res.data);
        } catch (_) {
          // ignore
        }
      } else {
        message.error(result.error || (running ? t('healthCheckLogs.stopSchedulerFailed') : t('healthCheckLogs.startSchedulerFailed')));
      }
    } catch (error: any) {
      const fallback = running ? t('healthCheckLogs.stopSchedulerFailed') : t('healthCheckLogs.startSchedulerFailed');
      message.error(`${fallback}: ${error.message || ''}`);
    } finally {
      setSchedulerToggling(false);
    }
  };

  const getHealthStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Badge status="success" text={t('healthCheckLogs.healthy')} />;
      case 'unhealthy':
        return <Badge status="error" text={t('healthCheckLogs.unhealthy')} />;
      default:
        return <Badge status="default" text={t('healthCheckLogs.unknown')} />;
    }
  };

  const formatResponseTime = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const filteredServiceOptions = filters.tenant_code
    ? services.filter(service => service.tenant_code === filters.tenant_code)
    : services;

  const columns = [
    {
      title: t('healthCheckLogs.serviceName'),
      dataIndex: 'service_name',
      key: 'service_name',
      width: 200
    },
    {
      title: t('healthCheckLogs.tenant'),
      dataIndex: 'tenant_code',
      key: 'tenant_code',
      width: 140,
      render: (code: string) => (code ? <Tag color="blue">{code}</Tag> : '-')
    },
    {
      title: t('healthCheckLogs.healthStatus'),
      dataIndex: 'health_status',
      key: 'health_status',
      width: 120,
      render: (status: string) => getHealthStatusBadge(status)
    },
    {
      title: t('healthCheckLogs.responseTime'),
      dataIndex: 'response_time',
      key: 'response_time',
      width: 120,
      render: (time: number) => formatResponseTime(time)
    },
    {
      title: t('healthCheckLogs.errorMessage'),
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: t('healthCheckLogs.checkTime'),
      dataIndex: 'checked_at',
      key: 'checked_at',
      width: 180,
      render: (time: string) => <TimeDisplay utcTime={time} format="datetime" />
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={t('healthCheckLogs.title')}
        extra={
          <Space wrap>
            <span>{t('healthCheckLogs.retentionDays')}:</span>
            <InputNumber
              size="small"
              min={1}
              max={365}
              style={{ width: 80 }}
              value={retentionDraft ?? retentionDays}
              onChange={(v) => setRetentionDraft(typeof v === 'number' ? v : null)}
            />
            <Button
              size="small"
              type="primary"
              loading={retentionSaving}
              disabled={retentionDraft === retentionDays}
              onClick={handleSaveRetention}
            >
              {t('common.save')}
            </Button>
            <Popconfirm
              title={t('healthCheckLogs.confirmCleanupTitle')}
              description={t('healthCheckLogs.confirmCleanupDesc', { days: retentionDraft ?? retentionDays })}
              onConfirm={handleCleanupNow}
            >
              <Button size="small" loading={cleanupRunning} danger>
                {t('healthCheckLogs.cleanupNow')}
              </Button>
            </Popconfirm>
            <span style={{ marginLeft: 8 }}>
              {t('healthCheckLogs.scheduledHealthCheck')}：
              {schedulerLoading && !schedulerStatus
                ? t('common.loading')
                : schedulerStatus?.running
                  ? t('healthCheckLogs.statusOn')
                  : t('healthCheckLogs.statusOff')}
            </span>
            <Button
              size="small"
              type={schedulerStatus?.running ? 'default' : 'primary'}
              loading={schedulerToggling}
              onClick={handleToggleScheduler}
            >
              {schedulerStatus?.running ? t('healthCheckLogs.disableScheduler') : t('healthCheckLogs.enableScheduler')}
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 统计信息 */}
          {stats && (
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title={t('healthCheckLogs.totalChecks')}
                  value={stats.total}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('healthCheckLogs.healthyCount')}
                  value={stats.healthy_count}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('healthCheckLogs.unhealthyCount')}
                  value={stats.unhealthy_count}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('healthCheckLogs.avgResponseTime')}
                  value={stats.avg_response_time}
                  suffix="ms"
                  prefix={<QuestionCircleOutlined />}
                />
              </Col>
            </Row>
          )}

          {/* 筛选条件 */}
          <Card size="small" title={t('healthCheckLogs.filterConditions')}>
            <Row gutter={16}>
              <Col span={6}>
                <Select
                  placeholder="选择租户"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.tenant_code}
                  onChange={(value) => handleFilterChange('tenant_code', value)}
                >
                  {tenants.map(tenant => (
                    <Option key={tenant.code} value={tenant.code}>
                      {tenant.name ? `${tenant.name} (${tenant.code})` : tenant.code}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder={t('healthCheckLogs.selectTenant')}
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.tenant_code}
                  onChange={(value) => handleFilterChange('tenant_code', value)}
                >
                  {tenants.map(tenant => (
                    <Option key={tenant.code} value={tenant.code}>
                      {tenant.name ? `${tenant.name} (${tenant.code})` : tenant.code}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder={t('healthCheckLogs.selectService')}
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.service_id}
                  onChange={(value) => handleFilterChange('service_id', value)}
                >
                  {filteredServiceOptions.map(service => (
                    <Option key={service.id} value={service.id}>
                      {service.service_name}
                      {service.tenant_code ? ` (${service.tenant_code})` : ''}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder={t('healthCheckLogs.selectHealthStatus')}
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.health_status}
                  onChange={(value) => handleFilterChange('health_status', value)}
                >
                  <Option value="healthy">{t('healthCheckLogs.healthy')}</Option>
                  <Option value="unhealthy">{t('healthCheckLogs.unhealthy')}</Option>
                  <Option value="unknown">{t('healthCheckLogs.unknown')}</Option>
                </Select>
              </Col>
              <Col span={6}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setFilters({
                      tenant_code: undefined,
                      service_id: undefined,
                      health_status: undefined,
                      start_date: undefined,
                      end_date: undefined
                    });
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                >
                  {t('common.reset')}
                </Button>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <RangePicker
                  style={{ width: '100%' }}
                  showTime
                  onChange={handleDateRangeChange}
                />
              </Col>
            </Row>
          </Card>

          {/* 日志表格 */}
          <Table
            columns={columns}
            dataSource={logs}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
                  showTotal: (total) => `${t('common.total')} ${total} ${t('common.items')}`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || 50
                }));
              }
            }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default HealthCheckLogs;

